import React, { useState, useEffect } from "react";
import { FaTimes, FaCalendarAlt } from "react-icons/fa";
import PublicScheduler from "../publicScheduler/PublicScheduler";

const Brochures = () => {
  const brochures = [
    {
      id: 1,
      label: "Faith and Community",
      url: "https://rlcpublicassets.blob.core.windows.net/images/00_Current_Community.pdf",
    },
    {
      id: 2,
      label: "Creative Arts",
      url: "https://rlcpublicassets.blob.core.windows.net/images/00_Current_Creative_Arts.pdf",
    },
    {
      id: 3,
      label: "Elevate",
      url: "https://rlcpublicassets.blob.core.windows.net/images/00_Current_Elevate.pdf",
    },
    {
      id: 4,
      label: "Performance Music",
      url: "https://rlcpublicassets.blob.core.windows.net/images/00_Current_Performance_Music.pdf",
    },
    {
      id: 5,
      label: "Sport",
      url: "https://rlcpublicassets.blob.core.windows.net/images/00_Current_Sport.pdf",
    },
  ];

  const [selectedUrl, setSelectedUrl] = useState(brochures[0].url);
  const [loadedUrls, setLoadedUrls] = useState([brochures[0].url]);

  const [isMobile, setIsMobile] = useState(false);

  // NEW: full-page scheduler modal state
  const [showScheduler, setShowScheduler] = useState(false);

  const schedulerActive = false;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Open/close scheduler modal + scroll lock
  const openScheduler = () => {
    setShowScheduler(true);
    document.body.style.overflow = "hidden";
  };
  const closeScheduler = () => {
    setShowScheduler(false);
    document.body.style.overflow = "";
  };

  // Close scheduler on ESC
  useEffect(() => {
    if (!showScheduler) return;
    const onKey = (e) => e.key === "Escape" && closeScheduler();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showScheduler]);

  const handleClick = (url) => {
    if (!url) {
      alert("This brochure is not yet available.");
      return;
    }

    if (isMobile) {
      const link = document.createElement("a");
      link.href = url;
      link.download = url.split("/").pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setSelectedUrl(url);
      if (!loadedUrls.includes(url)) {
        setLoadedUrls((prev) => [...prev, url]);
      }
    }
  };

  return (
    <div className="p-4 font-frutiger">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-2 gap-3">
        <div className="flex flex-col gap-3">
          {" "}
          <img
            src="/rlc.png"
            alt="RLC Logo"
            width="160"
            className="mb-2 sm:mb-0"
          />
          <div className="text-2xl sm:text-3xl text-blue-2 text-center sm:text-left mb-4">
            Co-curricular program
          </div>
        </div>

        {/* NEW: Show scheduler button */}
        {schedulerActive && (
          <button
            type="button"
            onClick={openScheduler}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-2 text-slate-700 hover:bg-slate-50 shadow-sm transition"
            title="Show scheduler"
          >
            <FaCalendarAlt className="text-slate-600" />
            <span className="font-medium">Show scheduler</span>
          </button>
        )}
      </div>

      {/* Main layout */}
      {!isMobile ? (
        <div className="flex gap-6 h-[calc(100vh-6rem)]">
          {/* Sidebar icons */}
          <div className="flex flex-col gap-4 w-64 overflow-y-auto pl-2 pr-2 pt-2">
            {brochures.map(({ id, label, url }) => {
              const isSelected = selectedUrl === url;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleClick(url)}
                  className={`p-4 rounded-xl transition-transform duration-300 hover:scale-105 focus:outline-none ${
                    isSelected
                      ? "ring-2 ring-indigo-500 shadow-md bg-white"
                      : "bg-gray-50 hover:ring-1 hover:ring-indigo-300"
                  }`}
                  aria-label={`Open brochure: ${label}`}
                >
                  <div className="flex flex-col items-center">
                    <img
                      src={`/${id}.png`}
                      alt={label}
                      className="w-24 h-24 rounded-full object-cover"
                    />
                    <span className="mt-3 text-center text-sm font-medium text-gray-700">
                      {label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* PDF Viewer */}
          <div className="flex-grow h-full relative bg-gray-50 rounded shadow flex items-center justify-center text-gray-500 text-lg px-6">
            {selectedUrl ? (
              loadedUrls.map((url) => (
                <iframe
                  key={url}
                  src={`${url}#page=1&zoom=page-fit`}
                  title={`Brochure Viewer ${url}`}
                  className={`absolute top-0 left-0 w-full h-full border rounded shadow transition-opacity duration-300 ${
                    selectedUrl === url ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                  style={{ overflow: "auto" }}
                />
              ))
            ) : (
              <div className="text-center italic text-gray-600">
                Select a pillar to view program details
              </div>
            )}
          </div>
        </div>
      ) : (
        // Mobile layout
        <div className="flex flex-col gap-6 items-center mb-10">
          {brochures.map(({ id, label, url }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleClick(url)}
              className="p-4 rounded-xl bg-gray-50 hover:ring-1 hover:ring-indigo-300 w-full max-w-xs"
              aria-label={`Download brochure: ${label}`}
            >
              <div className="flex flex-col items-center">
                <img
                  src={`/${id}.png`}
                  alt={label}
                  className="w-24 h-24 rounded-full object-cover"
                />
                <span className="mt-3 text-center text-sm font-medium text-gray-700">
                  {label}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* FULL-PAGE SCHEDULER MODAL */}
      {showScheduler && schedulerActive && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[200] flex"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={closeScheduler}
          />

          {/* Panel */}
          <div className="relative z-[201] m-0 h-full w-full bg-white flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-slate-900 truncate">
                  Co‑curricular Scheduler
                </h2>
                <p className="text-xs text-slate-500">
                  Press Esc or click outside to close
                </p>
              </div>
              <button
                type="button"
                onClick={closeScheduler}
                className="inline-flex items-center rounded-md px-2 py-1.5 text-gray-600 hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2"
                aria-label="Close"
              >
                <FaTimes />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto">
              <PublicScheduler />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Brochures;
